import React from 'react';
import { ArrowRight, Play, Route, FileText } from 'lucide-react';
import { stats } from '../../data/mock';

// Updated to absolute path to ensure iPhone reliability
const APP_SCREENSHOT = "https://rumalogistics.com/ruma-app-preview.jpg";

const HeroSection = () => {
  return (
    <section className="relative min-h-[90vh] lg:min-h-screen flex items-center pt-24 md:pt-20 overflow-hidden bg-black">
      {/* Background Grid Pattern */}
      <div 
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage: `
            repeating-linear-gradient(0deg, transparent, transparent 1px, transparent 1px, transparent 60px),
            repeating-linear-gradient(-90deg, #fff, #fff 1px, transparent 1px, transparent 60px)
          `,
          backgroundSize: '100% 100%'
        }}
      />
      
      {/* Gradient Orb - Adjusted for mobile scale */}
      <div className="absolute top-1/4 right-0 md:right-1/4 w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-[#00FFD1]/10 rounded-full blur-[80px] md:blur-[150px] pointer-events-none" />
      
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-[7.6923%] py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-10 md:gap-16 items-center">
          
          {/* Left Content */}
          <div className="space-y-6 md:space-y-8 text-center lg:text-left">
            {/* Pocket Tag */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full">
              <span className="w-1.5 h-1.5 bg-[#00FFD1] rounded-full animate-pulse" />
              <span className="text-[#00FFD1] text-xs md:text-sm font-medium">Pocket Logistics Manager</span>
            </div>
            
            {/* Main Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-[66px] font-bold text-white leading-[1.2] md:leading-[1.1] tracking-tight">
              Distribution and Marketing Control.
              <span className="block text-[#00FFD1]">In Your Pocket.</span>
            </h1>
            
            {/* Description */}
            <p className="text-base md:text-lg lg:text-xl text-white/80 leading-relaxed max-w-xl mx-auto lg:mx-0">
              Scan nearby leads, capture store GPS coordinates, and add prospecting notes instantly. 
              Optimize routes, track van inventory, and generate invoices in seconds.
            </p>
            
            {/* Buttons - Swapped to "Request a Demo" and added subtext */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4 items-center lg:items-start">
              <div className="w-full sm:w-auto">
                <a
                  href="#concierge"
                  className="bg-[#00FFD1] text-black px-8 py-4 font-bold text-lg inline-flex items-center justify-center sm:justify-between gap-3 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] group w-full sm:min-w-[220px]"
                >
                  Request a Demo
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </a>
                <p className="text-white/40 text-[10px] mt-2 text-center uppercase tracking-widest font-semibold">
                  Personalized Walkthrough • Custom Setup Available
                </p>
              </div>

              <a
                href="#demo-video"
                className="bg-white/5 backdrop-blur-sm text-white border border-white/10 px-8 py-4 font-bold text-lg inline-flex items-center justify-center gap-3 transition-all duration-300 hover:bg-white/10 w-full sm:min-w-[220px] h-[60px]"
              >
                <Play className="w-5 h-5" />
                Watch Demo
              </a>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 pt-8 border-t border-white/10">
              {stats.map((stat, index) => (
                <div key={index} className="text-center lg:text-left">
                  <div className="text-2xl md:text-3xl font-bold text-[#00FFD1]">{stat.value}</div>
                  <div className="text-white/50 text-[10px] md:text-xs uppercase tracking-wider mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Right Content */}
          <div className="relative block mt-12 lg:mt-0">
            <div className="relative z-10">
              <div className="bg-[#1a1a1a] rounded-[30px] md:rounded-[40px] p-2 md:p-3 shadow-2xl shadow-[#00FFD1]/10 border border-white/10 max-w-[280px] md:max-w-[320px] lg:max-w-none mx-auto transition-transform hover:scale-[1.01]">
                <div className="rounded-[24px] md:rounded-[32px] overflow-hidden">
                  <img 
                    src={APP_SCREENSHOT} 
                    alt="Ruma Logistics Management App"
                    className="w-full h-auto object-cover"
                    style={{ maxHeight: '600px' }}
                  />
                </div>
              </div>
              
              <div className="hidden md:block absolute -right-4 top-32 bg-[#121212] border border-white/10 p-4 rounded-lg shadow-xl animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#00FFD1]/20 rounded flex items-center justify-center">
                    <Route className="w-5 h-5 text-[#00FFD1]" />
                  </div>
                  <div>
                    <p className="text-white/60 text-xs">Route Optimized</p>
                    <p className="text-white font-semibold">12 Stops → 8</p>
                  </div>
                </div>
              </div>
              
              <div className="hidden md:block absolute -left-8 bottom-40 bg-[#121212] border border-white/10 p-4 rounded-lg shadow-xl animate-float-delayed">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/20 rounded flex items-center justify-center">
                    <FileText className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-white/60 text-xs">Invoice Created</p>
                    <p className="text-white font-semibold">$247.50</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </section>
  );
};

export default HeroSection;